import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { dateInputValue, money } from '../../lib/format.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { DialogFooter, DialogHeader, Modal } from '../Modal.js'
import { Button, ErrorState, Field, Input, Select, humanise } from '../ui.js'

/**
 * Recording money that has already arrived.
 *
 * Not taking a payment — there is no gateway behind this and the mockup's
 * "Take payment" button stays out until there is. This writes down a transfer
 * that landed in the bank or a card that was tapped on a terminal, which is
 * how the office has always closed an invoice.
 */

const METHODS = [
  'BANK_TRANSFER',
  'CARD',
  'CASH',
  'CHEQUE',
  'DIRECT_DEBIT',
  'ACCOUNT_CREDIT',
  'OTHER',
] as const

export function PaymentPopup({
  invoiceId,
  number,
  owing,
  open,
  onClose,
}: {
  invoiceId: string
  number: string
  owing: number
  open: boolean
  onClose: () => void
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="payment-popup-title" className="max-w-[460px]">
      {open ? (
        <Body invoiceId={invoiceId} number={number} owing={owing} onClose={onClose} />
      ) : null}
    </Modal>
  )
}

function Body({
  invoiceId,
  number,
  owing,
  onClose,
}: {
  invoiceId: string
  number: string
  owing: number
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  // Paying the balance in full is the overwhelmingly common case, so the
  // amount starts there and is corrected on the rare occasion it is part.
  const [amount, setAmount] = useState(() => (owing / 100).toFixed(2))
  const [method, setMethod] = useState<string>('BANK_TRANSFER')
  const [receivedAt, setReceivedAt] = useState(() => dateInputValue(new Date()))
  const [reference, setReference] = useState('')

  const record = useMutation(
    trpc.payments.record.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.invoices.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        onClose()
      },
    }),
  )

  const minorUnits = Math.round(Number(amount) * 100)
  const valid = Number.isFinite(minorUnits) && minorUnits !== 0

  return (
    <>
      <DialogHeader
        id="payment-popup-title"
        title="Record payment"
        aside={<span className="font-mono">{number}</span>}
        onClose={onClose}
      />

      <div className="grid min-h-0 flex-1 gap-3 overflow-y-auto p-4 sm:grid-cols-2">
        <Field label="Amount" hint={`${money(owing)} owing`} htmlFor="payment-amount">
          <Input
            id="payment-amount"
            type="number"
            step="0.01"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>

        <Field label="Method" htmlFor="payment-method">
          <Select
            id="payment-method"
            value={method}
            onChange={(event) => setMethod(event.target.value)}
          >
            {METHODS.map((option) => (
              <option key={option} value={option}>
                {humanise(option)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Received" htmlFor="payment-received">
          <Input
            id="payment-received"
            type="date"
            value={receivedAt}
            onChange={(event) => setReceivedAt(event.target.value)}
          />
        </Field>

        <Field label="Reference" hint="Optional" htmlFor="payment-reference">
          <Input
            id="payment-reference"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder="Receipt or transfer number"
          />
        </Field>

        {record.error ? (
          <div className="sm:col-span-2">
            <ErrorState message={errorMessage(record.error)} />
          </div>
        ) : null}
      </div>

      <DialogFooter>
        <Button size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={!valid}
          loading={record.isPending}
          onClick={() =>
            record.mutate({
              invoiceId,
              amount: minorUnits,
              method: method as (typeof METHODS)[number],
              receivedAt: new Date(`${receivedAt}T00:00`),
              reference: reference.trim() === '' ? null : reference.trim(),
            })
          }
        >
          Record payment
        </Button>
      </DialogFooter>
    </>
  )
}

